import { Link, navigate } from "gatsby";
import React, { useState } from "react";
import { useLocation } from "@reach/router";
import styled from "styled-components";
import ExternalArrow from "../../../assets/icon-colors/mutibrand-icons/icon-external-arrow.svg";
import InternalRightArrow from "../../../assets/icon-colors/mutibrand-icons/icon-internal-right-arrow.svg";
import InternalLeftArrow from "../../../assets/icon-colors/mutibrand-icons/icon-internal-left-arrow.svg";
import InternalIndicationArrow from "../../../assets/icon-colors/mutibrand-icons/icon-internal-indication-arrow.svg";
import HomeIcon from "../../../assets/icon-colors/mutibrand-icons/iconhome.svg";
import { WRAPPER_WIDE_MAX_WIDTH } from "../../../styles/shared/layout";
import useWindowSize from "../../../util/hooks/useWindowSize";
import { scrollToSection } from "../../../util/scrollToSection";
import {
  ink3,
  brandBlueDark,
  neutralsGrey,
  lightGrey2,
  offWhite,
  lightblue2,
  white,
} from "../../../styles/shared/colors";
import ExternalLinkModal from "../ExternalLinkModal";

const MultiBrandNavWrapper = styled.div`
  transition: transform 0.3s ease;

  ${({ mobileView }) =>
    mobileView === "child" &&
    `
      transform: translateX(-100%);
  `}

  ${(props) =>
    props.type === "utility"
      ? `
            display: flex;
            gap: 15px;
            align-items: center;
            @media(max-width: ${WRAPPER_WIDE_MAX_WIDTH}){
                display: none;
            }
            `
      : `
                display: none;
                    @media(max-width: ${WRAPPER_WIDE_MAX_WIDTH}){
                    display: flex;
                    flex-direction:column;
                    padding: 20px 0;
                }
            `}
`;

const SlideWrapper = styled.div`
  animation: ${({ direction }) =>
    direction === "forward"
      ? "slideInRight .45s ease"
      : "slideInLeft .45s ease"};

  @keyframes slideInRight {
    from {
      transform: translateX(100%);
    }
    to {
      transform: translateX(0);
    }
  }

  @keyframes slideInLeft {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(0);
    }
  }
`;

const MultiBrandNavLink = styled(Link)`
  display: flex;
  justify-content: center;
  gap: 8px;
  align-items: center;
  font-family: "proxima-nova";
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  text-decoration: none;
  color: ${ink3};
  .icon-external-arrow {
    width: 14px;
    height: 20px;
    background-image: url(${ExternalArrow});
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }
  &:hover {
    text-decoration: underline;
  }
  .homeIcon {
    width: 18px;
    height: 18px;
    background-image: url(${HomeIcon});
    background-repeat: no-repeat;
    background-size: contain;
  }
  @media (max-width: ${WRAPPER_WIDE_MAX_WIDTH}) {
    padding: 12px 20px;
    justify-content: flex-start;
    font-size: 16px;
    line-height: 20px;
    font-weight: 700;
    gap: 20px;

    .icon-external-arrow{
      width: 18px;
      height: 20px;
    }
  }
`;

const MultiBrandNavList = styled.div`
  display: flex;
  padding: 20px 0;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  border-bottom: 1px solid ${neutralsGrey};
`;

const MultiBrandNavSubLink = styled(Link)`
  display: flex;
  width: 100%;
  // min-height: 44px;
  padding: 12px 20px;
  justify-content: space-between;
  align-items: center;
  color: ${brandBlueDark};
  font-size: 16px;
  font-family: "proxima-nova";
  font-weight: 700;
  line-height: 20px;
  text-decoration: none;
  background: ${white};
  border: none;

  &:active {
    background: ${offWhite};
  }

  &:focus-visible {
    background: ${white};
    box-shadow: 0 0 24px 0 ${lightblue2};
    border: none;
    outline: none;
  }

  .internal-right-arrow {
    width: 18px;
    height: 20px;
    background-image: url(${InternalRightArrow});
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }
  .internal-indication-arrow {
    width: 18px;
    height: 20px;
    background-image: url(${InternalIndicationArrow});
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }
`;

const MultiBrandChildList = styled.div`
  display: flex;
  padding-bottom: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  align-self: stretch;
  border-bottom: 1px solid ${neutralsGrey};
`;

const MultiBrandNavBackLink = styled(Link)`
  display: flex;
  width: 100%;
  height: 64px;
  min-height: 44px;
  padding: 12px 20px;
  -webkit-box-pack: justify;
  -webkit-box-align: center;
  align-items: center;
  gap: 12px;
  justify-content: flex-start;
  color: ${brandBlueDark};
  font-size: 16px;
  font-weight: 800;
  line-height: 20px;
  border-bottom: 1px solid ${neutralsGrey};
  background: ${lightGrey2};
  border: none;

  .internal-left-arrow {
    width: 9px;
    height: 17px;
    background-image: url(${InternalLeftArrow});
    background-repeat: no-repeat;
    background-size: contain;
    background-position: center;
  }
`;


export default function MultiBrandNav({
  className,
  type,
  navItems,
  mobileView,
  setMobileView,
  selectedNav,
  setSelectedNav,
  setOpenMenu,
  animationDirection,
  setAnimationDirection,
}) {
  const [showModal, setShowModal] = useState(false);
  const [modalLink, setModalLink] = useState("");
  // const hasAnchors = navItems?.item?.anchors?.length > 0;
  const location = useLocation();
  // const hideAskMerck = shouldHideAskMerck(location.pathname);

  const { width } = useWindowSize();

  const handleMedicalClick = (e) => {
    e.preventDefault(); // stop navigation
    setModalLink(
      "https://www.merckmedicalportal.com/s/medical-information?AN=P5mzpu8oRgks2pD&HCP=m7pcsRtrbIqUITdRC=d0hMzkhIrEzIQPb",
    );
    setShowModal(true);
  };

  return (
    <>
      {mobileView === "main" && (
        <SlideWrapper direction={animationDirection}>
          <MultiBrandNavList>
            {navItems?.map((item) => {
              const hasAnchors = item?.anchors?.length > 0;

              return (
                <MultiBrandNavSubLink
                  key={item.label}
                  as="button"
                  type="button"
                  onClick={(e) => {
                    if (hasAnchors) {
                      setAnimationDirection("forward");
                      setSelectedNav(item);
                      setMobileView("child");
                    } else {
                      navigate(item.path);
                      setMobileView("main");
                      setOpenMenu(true);
                    }
                  }}
                >
                  {item.label}

                  <i
                    className={
                      hasAnchors
                        ? "internal-right-arrow"
                        : "internal-indication-arrow"
                    }
                  />
                </MultiBrandNavSubLink>
              );
            })}
          </MultiBrandNavList>
        </SlideWrapper>
      )}
      {mobileView === "child" && (
        <SlideWrapper direction={animationDirection}>
          <MultiBrandChildList>
            <MultiBrandNavBackLink
              as="button"
              onClick={() => {
                setAnimationDirection("back");
                setMobileView("main");
              }}
            >
              <i className="internal-left-arrow"></i> Back
            </MultiBrandNavBackLink>

            {selectedNav?.anchors?.map((link) => (
              <MultiBrandNavSubLink
                key={link.id}
                as="button"
                onClick={(e) => {
                  e.preventDefault();

                  const currentPath = window.location.pathname;
                  const normalize = (path) => path.replace(/\/+$/, "");

                  if (normalize(currentPath) === normalize(selectedNav.path)) {
                    setTimeout(() => {
                      requestAnimationFrame(() => {
                        scrollToSection(link.id);
                      });
                      setOpenMenu(true);
                    }, 100);
                  } else {
                    navigate(`${selectedNav.path}#${link.id}`);
                  }

                  setAnimationDirection("back");
                  setMobileView("main");
                  setOpenMenu(true);
                }}
              >
                {link.label}
                <i className="internal-indication-arrow"></i>
              </MultiBrandNavSubLink>
            ))}
          </MultiBrandChildList>
        </SlideWrapper>
      )}
      <MultiBrandNavWrapper className={className} type={type}>
        {/* {!hideAskMerck && (
          <MultiBrandNavLink to="https://www.askmerck.com/" target="_blank">
            Ask Merck<i className="icon-external-arrow"></i>
          </MultiBrandNavLink>
        )} */}

        {width <= 1024 && (
          <MultiBrandNavLink
            to="/"
            onClick={() => {
              setOpenMenu(true); // menu is close
              setMobileView("main");
            }}
          >
            Home <i className="homeIcon"></i>
          </MultiBrandNavLink>
        )}

        <MultiBrandNavLink to="#" onClick={handleMedicalClick}>
          Medical Information <i className="icon-external-arrow"></i>
        </MultiBrandNavLink>
        <MultiBrandNavLink to="https://www.welireg.com/" target="_blank">
          Visit Patient Site<i className="icon-external-arrow"></i>
        </MultiBrandNavLink>
      </MultiBrandNavWrapper>
      <ExternalLinkModal
        link={modalLink}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
      />
    </>
  );
}